import { useCallback, useSyncExternalStore } from 'react'
import {
  SESSION_DATA_IDENTIFIER,
  subscribeToSessionChanges,
  notifySessionChange,
} from './sessionToken.utils'

type SessionData = {
  token?: string
}

// Cached snapshot for referential stability (required by useSyncExternalStore).
// Only re-parses when the raw localStorage string changes.
let cachedRaw: string | null = null
let cachedParsed: SessionData | undefined = undefined

function getSnapshot(): SessionData | undefined {
  const raw = localStorage.getItem(SESSION_DATA_IDENTIFIER)
  if (raw !== cachedRaw) {
    cachedRaw = raw
    if (raw) {
      try {
        cachedParsed = JSON.parse(raw) as SessionData
      } catch {
        cachedParsed = undefined
      }
    } else {
      cachedParsed = undefined
    }
  }
  return cachedParsed
}

/**
 * Hook for managing session data in localStorage.
 *
 * Uses useSyncExternalStore so that all instances share the same snapshot.
 * When any instance calls setData, every instance re-renders with the new value.
 * This ensures that high-level providers (e.g. AppProviderPreloader) are notified
 * when a lower component (e.g. the login page) stores a session token.
 *
 * The pub/sub mechanism lives in sessionToken.utils so that imperative callers
 * (e.g. removeSessionData used during logout) can also notify subscribers.
 */
export const useSessionData = () => {
  const data = useSyncExternalStore(subscribeToSessionChanges, getSnapshot)

  const setData = useCallback((value: SessionData | undefined) => {
    if (value !== undefined && value !== null) {
      localStorage.setItem(SESSION_DATA_IDENTIFIER, JSON.stringify(value))
    } else {
      localStorage.removeItem(SESSION_DATA_IDENTIFIER)
    }
    notifySessionChange()
  }, [])

  return { data, setData }
}
